import type { FieldType, ICollection, IEvent, IEventAddress, IMapLocation } from '@/interfaces'
import { addressClass } from '@/interfaces'
import { Collection, EventAddress, Project } from '@/models'

import { addToJSON } from './helper'
export class Event implements IEvent {
	[key: string]: FieldType
	id: string | number
	code: string
	name: string
	addresses: ICollection<EventAddress>
	projects: ICollection<Project>
	startDate: Date
	endDate: Date
	createBy?: string
	createDate: Date
	updateBy?: string
	updateDate: Date
	type: number | string
	isDeleted?: boolean
	alias?: string
	jobCount: number

	constructor({
		id,
		code,
		name,
		addresses,
		projects,
		startDate,
		endDate,
		createBy,
		createDate,
		updateBy,
		updateDate,
		type = 0,
		isDeleted,
		alias,
		jobCount = 0
	}: Partial<IEvent> = {}) {
		this.id = id || 0
		this.code = code || ''
		this.name = name || ''
		this.addresses = new Collection(EventAddress, addresses || [])
		this.projects = new Collection(Project, projects || [])
		this.startDate = startDate ?? new Date()
		// If endDate is null/undefined, use startDate
		this.endDate = endDate ?? this.startDate
		this.createBy = createBy
		this.createDate = createDate ?? new Date()
		this.updateBy = updateBy
		this.updateDate = updateDate ?? new Date()
		this.type = type
		this.isDeleted = isDeleted ?? false
		this.alias = alias
		this.jobCount = jobCount
		addToJSON(Event)
	}

	public get dateRange(): string {
		const startDate = this.startDate ? new Date(this.startDate) : null
		const endDate = this.endDate ? new Date(this.endDate) : null

		const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' }

		const formattedStartDate = startDate ? startDate.toLocaleDateString(undefined, options) : 'N/A'
		const formattedEndDate = endDate ? endDate.toLocaleDateString(undefined, options) : 'N/A'

		// If start and end dates are the same, only show one date
		if (formattedStartDate === formattedEndDate) {
			return formattedStartDate
		}

		return `${formattedStartDate} - ${formattedEndDate}`
	}
	public get fullAddress(): string {
		const addressParts = []

		if (this.city) addressParts.push(this.city)
		if (this.state) addressParts.push(this.state)
		if (this.country) addressParts.push(this.country)

		const output = addressParts.join(', ')
		if (output.length > 0) return output
		else return 'N/A'
	}
	public get primaryAddress(): IEventAddress | undefined {
		return this.addresses?.find((a) => a.isPrimary) || undefined
	}

	// public get jobCount(): string {
	// 	return `3 associated jobs`
	// }

	public get mapLocations(): Array<IMapLocation> {
		const out = Array<IMapLocation>()
		if (this.addresses && this.addresses.length > 0) {
			this.addresses.forEach((address) => {
				if (address.mapLocation) out.push(address.mapLocation)
			})
		}
		return out
	}

	public get location(): string | undefined {
		return this.addresses?.length ? (this.addresses[0]?.name ?? '') : ''
	}
	public set location(value: string | undefined) {
		if (value) this.addresses[0].name = value
	}
	public get address1(): string | undefined {
		return this.addresses?.length ? (this.addresses[0]?.address1 ?? '') : ''
	}
	public set address1(value: string | undefined) {
		if (value) this.addresses[0].address1 = value
	}

	public get address2(): string | undefined {
		return this.addresses?.length ? (this.addresses[0]?.address2 ?? '') : ''
	}
	public set address2(value: string | undefined) {
		if (value) this.addresses[0].address2 = value
	}

	public get address3(): string | undefined {
		return this.addresses?.length ? (this.addresses[0]?.address3 ?? '') : ''
	}
	public set address3(value: string | undefined) {
		if (value) this.addresses[0].address3 = value
	}

	public get city(): string | undefined {
		return this.addresses?.length ? (this.addresses[0]?.city ?? '') : ''
	}
	public set city(value: string | undefined) {
		if (value) this.addresses[0].city = value
	}

	public get state(): string | undefined {
		return this.addresses?.length ? (this.addresses[0]?.state ?? '0') : '0'
	}
	public set state(value: string | undefined) {
		if (value) this.addresses[0].state = value
	}

	public get zip(): string | undefined {
		return this.addresses?.length ? (this.addresses[0].zip ?? '') : ''
	}
	public set zip(value: string | undefined) {
		if (value) this.addresses[0].zip = value
	}

	public get country(): string | undefined {
		return this.addresses?.length ? (this.addresses[0].country ?? '0') : '0'
	}
	public set country(value: string | undefined) {
		if (value) this.addresses[0].country = value
	}
	public get placeId(): string | undefined {
		return this.addresses?.length ? (this.addresses[0].placeId ?? '') : ''
	}
	public set placeId(value: string | undefined) {
		if (value) this.addresses[0].placeId = value
	}

	public get mapOverride(): boolean | undefined {
		return this.addresses?.length ? this.addresses[0]?.mapOverride : undefined
	}
	public set mapOverride(value: boolean | undefined) {
		if (this.addresses?.length) {
			this.addresses[0].mapOverride = value
		}
	}

	toJSON(): string {
		// This will be overwritten by the helper function
		return ''
	}
}
